/**
 * Community Posts API Client
 *
 * API Endpoints:
 * - GET /posts → PostListResponse
 * - GET /posts/{id} → Post
 * - POST /posts → Post
 * - POST /posts/{id}/like → { likes_count, dislikes_count }
 * - POST /posts/{id}/dislike → { likes_count, dislikes_count }
 * - DELETE /posts/{id} → void
 * - GET /post-comments/posts/{id} → PostCommentListResponse
 * - POST /post-comments/posts/{id} → PostComment
 */

import { isValidCoordinate } from '@/shared/utils/coordinates';
import type { Post, PostComment, PostCommentListResponse, PostListResponse } from '../types';
import { apiClient } from './base-client';

/** Ensure `post_image` and `post_audio` are always arrays regardless of backend shape */
function normalizePost(post: Post): Post {
  return {
    ...post,
    post_image: Array.isArray(post.post_image) ? post.post_image : [],
    post_audio: Array.isArray(post.post_audio) ? post.post_audio : [],
  };
}

export interface CreatePostParams {
  caption: string;
  images?: File[];
  audioFiles?: File[];
  location?: string;
  latitude?: number;
  longitude?: number;
}

export interface UpdatePostParams {
  caption?: string;
  /** Mix of existing image URLs to keep and new File objects to upload */
  post_image?: (string | File)[];
  /** false = remove existing audio */
  keep_audio?: boolean;
  /** New audio file to upload */
  post_audio?: File;
  location?: string;
  latitude?: number;
  longitude?: number;
}

export const postsClient = {
  list: async (params?: { page?: number }): Promise<PostListResponse> => {
    const data = await apiClient.get<{ posts?: PostListResponse }>(
      '/posts',
      params ? { params } : undefined
    );
    return data.posts || (data as PostListResponse);
  },

  getByUuid: async (uuid: string): Promise<Post> => {
    const data = await apiClient.get<{ post?: Post; data?: Post }>(`/posts/${uuid}`);
    return normalizePost(data.post || data.data || (data as Post));
  },

  get: async (id: number, serverToken?: string): Promise<Post> => {
    const data = await apiClient.get<{ posts?: PostListResponse; post?: Post; data?: Post }>(
      `/posts/${id}`,
      serverToken ? { serverToken } : undefined
    );
    const post = data.posts?.data?.[0] ?? data.post ?? data.data ?? (data as Post);
    return normalizePost(post);
  },

  create: async (params: CreatePostParams): Promise<Post> => {
    const formData = new FormData();
    formData.append('caption', params.caption);
    if (params.images) {
      for (const img of params.images) {
        formData.append('post_image[]', img);
      }
    }
    if (params.audioFiles) {
      for (const audio of params.audioFiles) {
        formData.append('post_audio[]', audio);
      }
    }
    if (params.location) formData.append('location', params.location);
    if (isValidCoordinate(params.latitude)) formData.append('latitude', String(params.latitude));
    if (isValidCoordinate(params.longitude)) formData.append('longitude', String(params.longitude));

    const data = await apiClient.post<{ post?: Post; data?: Post }>('/posts', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return normalizePost(data.post || data.data || (data as Post));
  },

  update: async (uuid: string, params: UpdatePostParams): Promise<Post> => {
    const formData = new FormData();
    if (params.caption !== undefined) formData.append('caption', params.caption);
    params.post_image?.forEach((item) => {
      if (typeof item === 'string') formData.append('keep_image_urls[]', item);
      else formData.append('post_image[]', item);
    });
    if (params.keep_audio !== undefined)
      formData.append('keep_audio', params.keep_audio ? '1' : '0');
    if (params.post_audio) formData.append('post_audio[]', params.post_audio);
    if (params.location !== undefined) formData.append('location', params.location);
    if (isValidCoordinate(params.latitude)) formData.append('latitude', String(params.latitude));
    if (isValidCoordinate(params.longitude)) formData.append('longitude', String(params.longitude));

    const data = await apiClient.post<{ post?: Post; data?: Post }>(
      `/posts/${uuid}/update`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return normalizePost(data.post || data.data || (data as Post));
  },

  like: async (id: number): Promise<{ likes_count: number; dislikes_count: number }> => {
    return apiClient.post<{ likes_count: number; dislikes_count: number }>(`/posts/${id}/like`);
  },

  dislike: async (id: number): Promise<{ likes_count: number; dislikes_count: number }> => {
    return apiClient.post<{ likes_count: number; dislikes_count: number }>(`/posts/${id}/dislike`);
  },

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/posts/${id}`);
  },

  getComments: async (
    id: number,
    params?: { page?: number },
    serverToken?: string
  ): Promise<PostCommentListResponse> => {
    const config: Record<string, unknown> = {};
    if (params) config.params = params;
    if (serverToken) config.serverToken = serverToken;
    const data = await apiClient.get<{
      postComment?: PostCommentListResponse;
      comments?: PostCommentListResponse;
    }>(`/post-comments/posts/${id}`, Object.keys(config).length ? config : undefined);
    return data.postComment || data.comments || (data as PostCommentListResponse);
  },

  addComment: async (postId: number, content: string, parentId?: number): Promise<PostComment> => {
    const data = await apiClient.post<{ postComment?: PostComment; data?: PostComment }>(
      `/post-comments/posts/${postId}`,
      {
        content,
        ...(parentId && { parent_id: parentId }),
      }
    );
    return data.postComment || data.data || (data as PostComment);
  },

  likeComment: async (commentId: number): Promise<void> => {
    await apiClient.post(`/post-comments/${commentId}/like-toggle`);
  },

  dislikeComment: async (commentId: number): Promise<void> => {
    await apiClient.post(`/post-comments/${commentId}/dislike`);
  },

  getCommentReplies: async (commentId: number): Promise<PostCommentListResponse> => {
    const data = await apiClient.get<{
      postComment?: PostCommentListResponse;
      comments?: PostCommentListResponse;
      data?: PostCommentListResponse;
    }>(`/post-comments/${commentId}/replies`);
    return (
      data.postComment || data.comments || data.data || (data as unknown as PostCommentListResponse)
    );
  },

  postComment: async (postId: number, content: string, parentId?: number): Promise<PostComment> => {
    return postsClient.addComment(postId, content, parentId);
  },

  deleteComment: async (commentUuid: string): Promise<void> => {
    await apiClient.delete(`/post-comments/${commentUuid}/delete`);
  },

  /**
   * Fetch a single post comment by numeric ID
   */
  getCommentById: async (id: number): Promise<PostComment> => {
    const data = await apiClient.get<{ postComment?: PostComment; data?: PostComment }>(
      `/post-comments/${id}`
    );
    return data.postComment || data.data || (data as PostComment);
  },
};
